import {createViewer, Viewer} from '@/utils/toastui'

/**
 * Markdown reduzido ao que cabe num card: uma linha só, com reticências, e com as
 * marcas que não mexem na tipografia.
 *
 * "Não mexem na tipografia" é a regra dura. O card tem altura orçada e fonte própria,
 * então nada vindo do markdown pode AUMENTAR a letra nem mudar a família. Daí o título
 * levar a escala da forma completa só para baixo — o H1 do tamanho do texto, os de baixo
 * menores (ver `md-h1`… no markdown.css) —, e código embutido virar um fundo discreto em
 * vez de monoespaçado.
 *
 * QUEM PARSEIA É O TOAST UI, não uma gramática escrita aqui.
 *
 * O ToastMark já está no bundle — é o mesmo parser que desenha o markdown na ficha e
 * na listagem —, só não é exposto como pacote. Então o caminho é usá-lo pelo Viewer e
 * LER O DOM que ele produz, classificando por tagName. Uma tentativa anterior com
 * regex errava exatamente onde markdown é traiçoeiro: `2 * 3 * 4` virava itálico,
 * `\*escapado\*` vazava a barra, título sublinhado com `===` vazava os sinais, link
 * por referência deixava a definição para trás. Nada disso é problema de quem delega
 * ao parser de verdade.
 */
export type MarkdownMark = 'bold' | 'italic' | 'strike' | 'code' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
/**
 * Um pedaço do excerto. `href`: o pedaço é texto de um link — o link de fora, ou uma referência do app
 * (o item, a coleção, a pessoa — ver `markdownMentions`), que o excerto desenha no padrão do app (pedido
 * do usuário). `linkStart`: o primeiro pedaço do link, onde vai a foto da pessoa ou o ícone da coleção —
 * um link com negrito no meio vira mais de um pedaço.
 */
export type MarkdownSegment = { text: string; marks: MarkdownMark[]; href?: string; linkStart?: boolean }

/** O link em que o texto está, ao descer pela árvore: o endereço, e se o primeiro pedaço já saiu. */
type Link = { href: string, started: boolean }

/** Entre blocos, o mesmo separador que o título do card usa entre as partes da chave. */
const SEPARATOR = ' · '

/**
 * Tags que viram marca. O título com o nível dele, e a mesma escala da forma completa
 * (pedido do usuário: era um negrito só, igual em todo nível) — ver o markdown.css.
 */
const MARK_OF: { [tag: string]: MarkdownMark } = {
    STRONG: 'bold', B: 'bold',
    EM: 'italic', I: 'italic',
    DEL: 'strike', S: 'strike', STRIKE: 'strike',
    CODE: 'code',
    H1: 'h1', H2: 'h2', H3: 'h3', H4: 'h4', H5: 'h5', H6: 'h6'
}

/**
 * Tags que valem uma quebra e por isso ganham separador.
 *
 * `BR` entra junto com os blocos: sem ele a quebra de linha não produzia NADA, e os
 * dois lados ficavam colados como se fossem a mesma frase.
 *
 * Isso vale para a quebra forçada e para a branda por igual, porque o ToastMark
 * desenha as duas como `<br>` — do DOM não dá para distinguir. E está certo assim: no
 * editor WYSIWYG as duas aparecem como uma quebra visível para quem escreve, então o
 * `·` é fiel ao que a pessoa viu. O preço é texto colado de fora com quebra rígida no
 * meio do parágrafo, que ganha um `·` no meio da frase.
 *
 * `UL`/`OL`/`TABLE` ficam de fora de propósito: quem carrega texto é o item e a célula,
 * e contar o contêiner também daria dois separadores para uma quebra só. `HR` também,
 * porque não tem texto nenhum e deixaria um separador solto.
 */
const BREAKS = [
    'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'BLOCKQUOTE', 'TD', 'TH', 'BR'
]

/** Blocos de código somem inteiros, como já sumiam: são ruído num excerto de 120. */
const SKIPPED = ['PRE']

/**
 * O Viewer é UM só, num elemento nunca anexado ao documento.
 *
 * Um por card seria o absurdo que o comentário do PropertyValue já recusava. Como
 * tudo aqui é síncrono, reusar a mesma instância entre cards é seguro: ninguém lê o
 * DOM dela no meio da escrita de outro.
 *
 * Criado sob demanda para que importar este módulo não instancie nada — o excerto só
 * existe no card, e as outras telas desenham o markdown de verdade.
 */
let viewer: Viewer | null = null
let host: HTMLElement | null = null

function parsedInto(markdown: string): HTMLElement {
    if (!viewer || !host) {
        host = document.createElement('div')
        //referenceDefinition fica no padrão (false) por DUAS razões, e a primeira é um
        //defeito: com ele ligado, o setMarkdown seguinte não limpa o documento
        //anterior, e o texto de um card reaparecia colado no próximo. A segunda é que
        //a ficha e a listagem também usam o padrão — assim o card mostra exatamente o
        //que elas mostram. O preço é link por referência ficar literal.
        viewer = createViewer(host)
    }
    viewer.setMarkdown(markdown)
    return host
}

/**
 * Espaço em branco achatado.
 *
 * O `\s+` que resta aqui normaliza espaço, e não interpreta markdown: é a indentação
 * do HTML e a quebra branda de parágrafo, que no card não podem virar espaço duplo
 * nem quebra de linha.
 */
function flatten(text: string): string {
    return text.replace(/\s+/g, ' ')
}

/** Os números de item de lista (`listNumber`): o parágrafo que o item traz dentro não os separa do texto. */
const numbers = new WeakSet<MarkdownSegment>()

function pushSeparator(out: MarkdownSegment[]): void {
    //Nem antes do primeiro bloco, nem dobrado quando um bloco aninha outro, nem entre o número de um item
    //e o texto dele — o Toast UI põe um parágrafo dentro do item
    const last = out[out.length - 1]
    if (!last || last.text === SEPARATOR || numbers.has(last)) return
    out.push({text: SEPARATOR, marks: []})
}

/**
 * Texto de um nó, já costurado com o anterior.
 *
 * Cada nó do DOM é achatado por conta própria, então o espaço entre dois deles pode
 * dobrar — é o que acontece em volta de uma imagem, que some e deixa o espaço dos
 * dois lados. Aqui o segundo espaço é descartado na emenda.
 */
function pushText(text: string, marks: MarkdownMark[], out: MarkdownSegment[], link?: Link): void {
    const previous = out.length ? out[out.length - 1].text : ''
    const glued = previous.endsWith(' ') && text.startsWith(' ') ? text.slice(1) : text
    if (!glued) return
    out.push(link ? {text: glued, marks, href: link.href, linkStart: !link.started} : {text: glued, marks})
    if (link) link.started = true
}

/**
 * O número de um item de lista numerada (pedido do usuário: a linha achatava a lista e o número
 * sumia) — a partir do `start` da lista, que o markdown `3.` no primeiro item põe. Na lista com
 * marcadores, nada: o `·` entre os itens já os separa.
 */
function listNumber(item: HTMLElement): string | undefined {
    const list = item.parentElement
    if (!list || list.tagName !== 'OL') return undefined
    const position = Array.from(list.children).filter(child => child.tagName === 'LI').indexOf(item)
    return `${Number(list.getAttribute('start') || 1) + position}. `
}

function walk(node: Node, marks: MarkdownMark[], out: MarkdownSegment[], link?: Link): void {
    node.childNodes.forEach(child => {
        if (child.nodeType === Node.TEXT_NODE) {
            const text = flatten(child.textContent || '')
            //Só espaço entre tags não é conteúdo; espaço DENTRO de um bloco é
            if (text.trim()) pushText(text, marks, out, link)
            return
        }
        if (child.nodeType !== Node.ELEMENT_NODE) return

        const element = child as HTMLElement
        const tag = element.tagName
        if (SKIPPED.indexOf(tag) >= 0) return
        if (BREAKS.indexOf(tag) >= 0) pushSeparator(out)
        const number = tag === 'LI' ? listNumber(element) : undefined
        if (number) {
            pushText(number, marks, out)
            numbers.add(out[out.length - 1])
        }

        const mark = MARK_OF[tag]
        const href = tag === 'A' ? element.getAttribute('href') : null
        walk(child, mark && marks.indexOf(mark) < 0 ? [...marks, mark] : marks, out,
            href ? {href, started: false} : link)
    })
}

/**
 * Corta em `max` caracteres VISÍVEIS: conta o texto, nunca a marcação. E não no meio de uma palavra
 * (relato do usuário: "domí…"): o pedaço volta até o último espaço; sem espaço nele, fica de fora —
 * meia palavra não diz nada, e as reticências já dizem que há mais. Só a primeira palavra do texto,
 * longa demais para caber, é cortada no meio: senão não sobraria nada.
 */
function truncate(segments: MarkdownSegment[], max: number): MarkdownSegment[] {
    const kept: MarkdownSegment[] = []
    let length = 0
    for (const segment of segments) {
        if (length + segment.text.length <= max) {
            kept.push(segment)
            length += segment.text.length
            continue
        }
        const room = segment.text.slice(0, max - length)
        const next = segment.text.charAt(max - length)
        const whole = /\s/.test(next) ? room : room.slice(0, Math.max(room.lastIndexOf(' '), 0))
        //Separador pela metade não vira nada: " ·…" é ruído, não informação
        const partial = segment.text === SEPARATOR
            ? '' : (whole.trimEnd() || (kept.length ? '' : room.trimEnd()))
        if (partial) kept.push({...segment, text: partial})
        //Reticências sem marca: são do corte, não do texto
        kept.push({text: '…', marks: []})
        return kept
    }
    return kept
}

/** O que o parse de um markdown rende: o excerto e se o documento é uma linha só. */
type Parsed = { segments: MarkdownSegment[]; singleLine: boolean }

/**
 * O documento é UMA LINHA: um parágrafo só, sem quebra e sem imagem. É o que se mostra inteiro
 * na linha sem perder nada — título, lista, citação, tabela, código e linha horizontal são
 * estrutura, que a linha achataria; a imagem o excerto descarta. Decidido aqui, pelo mesmo DOM do
 * excerto, para que "é uma linha" e "como fica a linha" nunca divirjam.
 */
function isSingleLine(doc: HTMLElement): boolean {
    const blocks = Array.from(doc.children)
    if (!blocks.length) return true
    return blocks.length === 1 && blocks[0].tagName === 'P' && !blocks[0].querySelector('br, img')
}

/**
 * Parse inteiro, memorizado pelo markdown de origem.
 *
 * Cada card pede o excerto E o texto puro do tooltip, em tamanhos diferentes — sem
 * isto seriam dois parses por card; e a ficha pergunta antes se o valor é uma linha. O teto
 * zera o cache em vez de expirar entrada por entrada: ele existe para a tela que está aberta,
 * e não como memória do app.
 */
const cache: { [markdown: string]: Parsed } = {}
let cached = 0
const CACHE_MAX = 200

function parse(markdown: string): Parsed {
    const hit = cache[markdown]
    if (hit) return hit
    const segments: MarkdownSegment[] = []
    const doc = parsedInto(markdown)
    //O Viewer põe o documento dentro de um contêiner seu; os blocos são os filhos dele
    const contents = doc.querySelector('.toastui-editor-contents') as HTMLElement || doc
    const singleLine = isSingleLine(contents)
    walk(doc, [], segments)
    //Separador no fim não separa nada: sobra de uma quebra forçada que encerra o texto
    while (segments.length && segments[segments.length - 1].text === SEPARATOR) segments.pop()
    //Espaço nas pontas: a primeira e a última marca herdam o espaço que separava os
    //nós vizinhos, e no card ele viraria um recuo sem motivo
    if (segments.length) {
        segments[0].text = segments[0].text.replace(/^ /, '')
        const last = segments[segments.length - 1]
        last.text = last.text.replace(/ $/, '')
    }
    if (cached >= CACHE_MAX) {
        Object.keys(cache).forEach(key => delete cache[key])
        cached = 0
    }
    const parsed = {segments, singleLine}
    cache[markdown] = parsed
    cached++
    return parsed
}

const isEmpty = (value: any) => value === undefined || value === null || value === ''

/** O excerto, cortado em `max` caracteres visíveis — `Infinity` mostra inteiro. */
export function markdownSegments(value: any, max = 120): MarkdownSegment[] {
    if (isEmpty(value)) return []
    return truncate(parse(String(value)).segments, max)
}

/**
 * O markdown é uma linha só (ver `isSingleLine`)? Então a ficha o mostra como campo, linearizado e
 * inteiro; senão, como seção, desenhado. Vazio é linha: continua campo, com o traço.
 */
export function isSingleLineMarkdown(value: any): boolean {
    return isEmpty(value) || parse(String(value)).singleLine
}

/**
 * O excerto de `max` caracteres mostra o markdown inteiro? Não quando ele tem estrutura (títulos,
 * listas, parágrafos — que a linha achata) nem quando é cortado. É quando a célula da grade oferece
 * ver o texto desenhado.
 */
export function excerptShowsAll(value: any, max = 120): boolean {
    if (isEmpty(value)) return true
    const {segments, singleLine} = parse(String(value))
    return singleLine && segments.reduce((length, segment) => length + segment.text.length, 0) <= max
}

/**
 * O mesmo excerto em texto puro, para o `title` — que não carrega marcação.
 *
 * Montado a partir dos segmentos, e não por um caminho próprio: é o que garante que o
 * que se lê no card e o que aparece ao parar o ponteiro nunca divirjam.
 */
export function markdownExcerpt(value: any, max = 120): string {
    return markdownSegments(value, max).map(segment => segment.text).join('')
}
